import { type DBSchema, openDB } from 'idb';

import type { PersistenceManager } from '../core/model/persistenceManager';

const DB_NAME = 'orionV3'; // change DB to avoid issues
const DB_VERSION = 1;
const IDENTITY_STORE = 'identity';

const IDENTITY_ID = 'userId';

interface OrionV3 extends DBSchema {
  [IDENTITY_STORE]: {
    key: string;
    value: {
      identity: string;
      updatedAt: string;
      userId: string;
    };
  };
}

/**
 * Create an Orion persistence manager leveraging indexedDB.
 */
export async function createIndexedDBPersistenceManager<
  Settings extends Record<string, unknown>,
>(): Promise<PersistenceManager<Settings>> {
  const db = await openDB<OrionV3>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      db.createObjectStore(IDENTITY_STORE, {
        keyPath: IDENTITY_ID,
      });
    },
  });

  return {
    clearEntities: async () => {
      await db.clear(IDENTITY_STORE);
    },
    loadIdentity: async (_userId: string) => {
      try {
        const queryResult = await db.get(IDENTITY_STORE, _userId);
        if (!queryResult) {
          return null;
        }

        const { identity, updatedAt, userId } = queryResult;
        if (!identity || !updatedAt || !userId) {
          return null;
        }
        return {
          identity: JSON.parse(identity),
          updatedAt: Number(updatedAt),
          userId,
        };
      } catch {
        return null;
      }
    },
    saveIdentity: async (identity, userId) => {
      await db.put(IDENTITY_STORE, {
        identity: JSON.stringify(identity),
        updatedAt: Date.now().toString(),
        userId,
      });
    },
  };
}
